import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, MessageSquareHeart } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

function renderContent(text) {
  const blocks = text.split("\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      out.push(<ul key={`ul-${key}`} className="my-4 space-y-2 list-none">{list.map((li, i) => (
        <li key={i} className="flex items-start gap-2.5 text-charcoal-700"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-terracotta-400 shrink-0" />{li}</li>
      ))}</ul>);
      list = [];
    }
  };
  blocks.forEach((line, idx) => {
    const t = line.trim();
    if (t.startsWith("## ")) { flush(idx); out.push(<h2 key={idx} className="font-serif text-2xl text-forest-900 mt-8 mb-2">{t.slice(3)}</h2>); }
    else if (/^\d+\.\s/.test(t)) { list.push(t.replace(/^\d+\.\s/, "")); }
    else if (t.startsWith("- ")) { list.push(t.slice(2)); }
    else if (t === "") { flush(idx); }
    else { flush(idx); out.push(<p key={idx} className="text-charcoal-700 leading-relaxed my-3">{t.replace(/\*\*/g, "")}</p>); }
  });
  flush("end");
  return out;
}

export default function PolicyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPolicy(null);
    api.get(`/policies/${slug}`).then((r) => setPolicy(r.data)).catch(() => setError(true));
  }, [slug]);

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-8">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All resources
        </Link>

        {error ? (
          <p className="text-charcoal-600">This resource could not be found.</p>
        ) : policy === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="policy-detail">
            <span className="text-[11px] uppercase tracking-wide text-terracotta-500 font-bold">{policy.category}</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">{policy.title}</h1>
            <p className="mt-4 text-lg text-charcoal-600 leading-relaxed">{policy.summary}</p>
            <div className="mt-8 border-t border-forest-600/10 pt-6">{renderContent(policy.content)}</div>

            <div className="mt-12 rounded-2xl bg-forest-50 border border-forest-200 p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <div className="flex items-start gap-3">
                <MessageSquareHeart className="h-7 w-7 text-terracotta-400 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-xl text-forest-900">Have a concern related to this?</p>
                  <p className="text-sm text-charcoal-600">The ER CoE is here to help — confidentially, anytime.</p>
                </div>
              </div>
              <Button onClick={() => navigate("/report")} data-testid="policy-cta-btn" className="rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white px-6 shrink-0">Share a Concern</Button>
            </div>
          </motion.div>
        )}
      </article>
    </Layout>
  );
}
